import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useLoginState, performLogout } from "../../hooks/useLoginState";
import { useTranslation } from "../../contexts/TranslationContext";
import Account from "./Account";
import "./navbar.scss";

const Navbar = () => {
  const [active, setActive] = useState("navBar");
  const [isLoggedIn] = useLoginState(); // Use the custom hook
  const { language } = useTranslation(); // Get translations
  const location = useLocation();

  const navigate = useNavigate();

  // Get current active nav item based on current route
  const getCurrentNavItem = () => {
    const pathname = location.pathname;
    if (pathname === '/' || pathname.includes('/evidence')) return 'evidence';
    if (pathname.includes('/gallery')) return 'evidence'; // Gallery maps to evidence
    if (pathname.includes('/evidence')) return 'evidence';
    if (pathname.includes('/retail')) return 'retail';
    if (pathname.includes('/import')) return 'import';
    if (pathname.includes('/analyze')) return 'analyze';
    if (pathname.includes('/history')) return 'history';
    if (pathname.includes('/profiles')) return 'profiles';
    return 'evidence'; // default fallback
  };

  const svgIconEvidence = (
    <svg
      
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="24"
      fill="none"
      viewBox="0 0 20 24"
    >
      {/* Outer rectangle frame */}
      <rect
        x="1"
        y="1"
        width="18"
        height="22"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeDasharray="0"
        fill="none"
        rx="2"
      />

      {/* Upper sole shape (toe area) */}
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1"
        fill="rgb(59, 130, 246, 0.1)"
        d="M10 3.795c-3.25 0-4.55 2.925-4.55 5.265 0 .936.26 1.755.52 2.574.13.468.26.936.26 1.287.13.351.26.702.52.936.39.351 1.04.468 1.69.468h3.12c.65 0 1.3-.117 1.69-.468.26-.234.39-.585.52-.936 0-.351.13-.819.26-1.287.26-.819.52-1.638.52-2.574 0-2.34-1.3-5.265-4.55-5.265z"
      />

      {/* Lower sole shape (heel area) */}
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1"
        fill="rgba(34, 197, 94, 0.15)"
        d="M7.324 15.524c0 .5616 0 1.1232.156 1.6848.156.7488.312 1.4976.624 2.0592.468.7488 1.248.7488 2.028.7488s1.56 0 2.028-.7488c.312-.5616.468-1.3104.624-2.0592.156-.5616.156-1.1232.156-1.6848H7.724z"
      />
    </svg>
  );

  const svgIconRetail = (
     <svg viewBox="0 -960 960 960" fill="currentColor" style={{border: '1px solid currentColor', borderRadius: '4px', padding: '2px'}}>
            {/* flip the object */}
            <g transform="scale(-1, 1) translate(-960, 0)">
            <path d="M216-580q39 0 74 14t64 41l382 365h24q17 0 28.5-11.5T800-200q0-8-1.5-17T788-235L605-418l-71-214-74 18q-38 10-69-14t-31-63v-84l-28-14-154 206q-1 1-1 1.5t-1 1.5h40Zm0 80h-46q3 7 7.5 13t10.5 11l324 295q11 11 25 16t29 5h54L299-467q-17-17-38.5-25t-44.5-8ZM566-80q-30 0-57-11t-50-31L134-417q-46-42-51.5-103T114-631l154-206q17-23 45.5-30.5T368-861l28 14q21 11 32.5 30t11.5 42v84l74-19q30-8 58 7.5t38 44.5l65 196 170 170q20 20 27.5 43t7.5 49q0 50-35 85t-85 35H566Z" />
            </g>
        </svg>
  );

  // SVG icons for navigation
  const svgIconGallery = (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      <rect x="3" y="3" width="18" height="12" rx="1" stroke="currentColor" strokeWidth="2" />
      <circle cx="8" cy="7" r="1.5" fill="currentColor" />
      <path d="M6 12L8 9L10 11" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      <path d="M10 12L12 8L16 12" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      <line x1="3" y1="19" x2="21" y2="19" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
      <line x1="3" y1="21" x2="21" y2="21" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
    </svg>
  );

  const svgIconImport = (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 7.757v8.486M7.757 12H16.243M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  );

  const svgIconCompare = (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4h6v16H4V4zm10 0h6v16h-6V4z" />
      <path stroke="currentColor" strokeLinecap="round" strokeWidth="2" d="M12 8h-1m1 4h-1m1 4h-1" />
    </svg>
  );

  // icon analyze is an magnifying glass
  const svgIconAnalyze = (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <line x1="16.65" y1="16.65" x2="20" y2="20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );

  const svgIconProfiles = (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      <path stroke="currentColor" strokeWidth="2" d="M7 17v1a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-1a3 3 0 0 0-3-3h-4a3 3 0 0 0-3 3Zm8-9a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  );

  const svgIconHistory = (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  );

  // icon user guide is like pdf document
  const svgIconUserGuide = (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
      <path stroke="currentColor" strokeWidth="2" d="M4 4v16a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V8l-4-4H5a1 1 0 0 0-1 1z" />
      <path stroke="currentColor" strokeWidth="2" d="M16 4v4h4" />
      <line x1="7" y1="10" x2="17" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="7" y1="13" x2="14" y2="13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="7" y1="16" x2="17" y2="16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <line x1="7" y1="19" x2="12" y2="19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );

  const currentNavItem = getCurrentNavItem();

  // Optional: Listen for login/logout in other tabs - now handled by useLoginState hook

  const navBarClick = e => {
    // No need to manage local state since we're using URL-based active state
  };

  const showNav = () => setActive("navBar activeNavBar");
  const closeNav = () => setActive("navBar");

  const handleLogout = () => {
    // Use the performLogout helper which handles localStorage and events
    performLogout();

    // Use navigate with relative path - this will respect the basename
    navigate("/login", { replace: true });
  };

  return (
    <section className="navBarSection">
      <div className="header flex">
        <div className="logoDiv">
          <label>Footwear</label>
          <label className="logoIQ">IQ</label>
        </div>
        {isLoggedIn &&
          <div className={active}>
            <ul className="navLists flex">
              {/* <li className="navItem">
                <NavLink to="/gallery" name="gallery" className={currentNavItem === 'gallery' ? 'active' : ''} onClick={navBarClick}>
                  <i class="fa fa-picture-o" aria-hidden="true"></i>
                  <label>{language.navigation?.gallery || 'Gallery'}</label>
                </NavLink>
              </li> */}
              <li className="navItem">
                <NavLink to="/retail" name="retail" className={currentNavItem === 'retail' ? 'active' : ''} onClick={navBarClick}>
                  {svgIconRetail}
                  <label>{language.navigation?.retail || 'Retail'}</label>
                </NavLink>
              </li>
              <li className="navItem">
                <NavLink to="/evidence" name="evidence" className={currentNavItem === 'evidence' ? 'active' : ''} onClick={navBarClick}>
                  {svgIconEvidence}
                  <label>{language.navigation?.evidence || 'Evidence'}</label>
                </NavLink>
              </li>
              <li className="navItem">
                <NavLink to="/import" name="import" className={currentNavItem === 'import' ? 'active' : ''} onClick={navBarClick}>
                  {svgIconImport}
                  <label>{language.navigation?.import || 'Import'}</label>
                </NavLink>
              </li>
              <li className="navItem">
                <NavLink to="/analyze" name="analyze" className={currentNavItem === 'analyze' ? 'active' : ''} onClick={navBarClick}>
                  {svgIconAnalyze}
                  <label>{language.navigation?.analyze || 'Analyze'}</label>
                </NavLink>
              </li>
              <li className="navItem">
                <NavLink to="/history" name="history" className={currentNavItem === 'history' ? 'active' : ''} onClick={navBarClick}>
                  {svgIconHistory}
                  <label>{language.navigation?.history || 'History'}</label>
                </NavLink>
              </li>
              <li className="navItem" style={{ display: 'none' }}>
                <NavLink to="/profiles" name="profiles" className={currentNavItem === 'profiles' ? 'active' : ''} onClick={navBarClick}>
                  {svgIconProfiles}
                  <label>{language.navigation?.profiles || 'Profiles'}</label>
                </NavLink>
              </li>
              <li className="navItem">
                <a href="https://drive.google.com/drive/folders/1sy_6EQjTN8fo7wsOCSwpJCd8fjXVsJ1b?usp=sharing" target="_blank" rel="noopener noreferrer" className={currentNavItem === 'guide' ? 'active' : ''} onClick={navBarClick}>
                  {svgIconUserGuide}
                  <label>{language.navigation?.userGuide || 'Guides'}</label>
                </a>
              </li>
            </ul>
            <div className="close-menu" onClick={closeNav}>
              <svg
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18 17.94 6M18 18 6.06 6"
                />
              </svg>
            </div>
          </div>
        }
        <div className="menu" onClick={showNav}>
          <i className="fa fa-bars" aria-hidden="true" />
        </div>

        {isLoggedIn &&
          <div className="account" title="Your account and settings">
            <Account handleLogout={handleLogout} />
          </div>
        }
      </div>
    </section>
  );
};

export default Navbar;
